import {
  AnimatePresence,
  motion,
  useDragControls,
  useMotionValue,
  useMotionValueEvent,
} from 'framer-motion';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useLoupeRegistry } from '../runtime/registry';
import { phaseAtTime, rangeOf } from '../runtime/phases';
import { useAnnotations } from '../annotations/AnnotationsProvider';
import { annotationsToMarkdown } from '../annotations/export';
import { Welcome } from './Welcome';
import { SampleScene, SAMPLE_SCENE_ID } from './SampleScene';
import { Tooltip } from './Tooltip';

/**
 * Loupe panel — the floating UI at app root, driven by the LoupeRegistry.
 *
 * Reads the active scene's TimelineState from the registry. The user picks
 * the scene to control via the dropdown; switching scenes triggers a brief
 * highlight overlay on the picked scene's root element so it's never
 * ambiguous which animation you're working on.
 */

const SPEED_OPTIONS = [0.25, 0.5, 1, 2, 4] as const;
const LOUPE_POSITION_KEY = 'loupe:panelPosition';
/** Stores the user's explicit scene pick — including `null` when
 *  they collapsed the panel. Loaded on mount so refresh doesn't
 *  un-collapse the panel by auto-selecting whatever scene registers
 *  first. */
const LOUPE_ACTIVE_SCENE_KEY = 'loupe:activeScene';

const FONT = 'var(--loupe-font, system-ui, -apple-system, sans-serif)';
const ACCENT = 'var(--loupe-accent, #3A97F9)';
const ACCENT_SOFT = 'var(--loupe-accent-soft, rgba(58, 151, 249, 0.28))';
const ACCENT_GLOW = 'var(--loupe-accent-glow, rgba(58, 151, 249, 0.6))';
const ACCENT_RING = 'var(--loupe-accent-ring, rgba(58, 151, 249, 0.18))';
const ACCENT_HALO = 'var(--loupe-accent-halo, rgba(58, 151, 249, 0.35))';
const ACCENT_TINT = 'var(--loupe-accent-tint, rgba(58, 151, 249, 0.7))';
const PANEL_BG = 'var(--loupe-panel-bg, rgba(18, 20, 25, 0.92))';
const PANEL_FG = 'var(--loupe-panel-fg, #E8EAEE)';
const PANEL_MUTED = 'var(--loupe-panel-muted, #9BA3AF)';
const PANEL_BORDER = 'var(--loupe-panel-border, rgba(255, 255, 255, 0.08))';
const PANEL_HIGHLIGHT = 'var(--loupe-panel-highlight, #EAF3FF)';

export function LoupePanel() {
  const registry = useLoupeRegistry();
  const [sampleActive, setSampleActive] = useState(false);
  const activeScene = registry.activeSceneId
    ? registry.scenes.find((s) => s.id === registry.activeSceneId)
    : undefined;

  // Hydrate the persisted scene selection once — BEFORE any auto-
  // select from scene registration has time to override it. Writing
  // `null` explicitly means "user picked None, stay collapsed".
  const sceneHydratedRef = useRef(false);
  useEffect(() => {
    if (sceneHydratedRef.current) return;
    sceneHydratedRef.current = true;
    try {
      const raw = localStorage.getItem(LOUPE_ACTIVE_SCENE_KEY);
      if (raw === null) return;
      const parsed = JSON.parse(raw) as { id: string | null };
      // null = deliberate collapsed state; a real id = restore it
      // if the scene is (or becomes) registered.
      if (parsed.id === null) {
        registry.setActiveSceneId(null);
      } else if (registry.scenes.some((s) => s.id === parsed.id)) {
        registry.setActiveSceneId(parsed.id);
      }
    } catch {
      /* corrupt storage — ignore */
    }
  }, [registry]);

  // Persist every explicit change so refreshes preserve it.
  useEffect(() => {
    try {
      localStorage.setItem(
        LOUPE_ACTIVE_SCENE_KEY,
        JSON.stringify({ id: registry.activeSceneId }),
      );
    } catch {
      /* ignore */
    }
  }, [registry.activeSceneId]);

  // Escape toggles the panel between collapsed (no scene) and the
  // last-selected real scene. Fast way to get the panel out of the
  // way mid-edit without reaching for the dropdown.
  const lastRealSceneIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (registry.activeSceneId) {
      lastRealSceneIdRef.current = registry.activeSceneId;
    }
  }, [registry.activeSceneId]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Only react when Loupe-relevant — skip when the user is
      // typing in an input, contenteditable, etc.
      const target = e.target as HTMLElement | null;
      if (target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      )) return;
      if (registry.activeSceneId) {
        registry.setActiveSceneId(null);
      } else if (lastRealSceneIdRef.current &&
                 registry.scenes.some((s) => s.id === lastRealSceneIdRef.current)) {
        registry.setActiveSceneId(lastRealSceneIdRef.current);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [registry]);

  const {
    pickerMode,
    setPickerMode,
    visible: annotationsVisible,
    setVisible: setAnnotationsVisible,
    annotations,
  } = useAnnotations();

  const [expanded, setExpanded] = useState(true);

  const dragControls = useDragControls();
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState(() => ({
    w: typeof window === 'undefined' ? 1440 : window.innerWidth,
    h: typeof window === 'undefined' ? 900 : window.innerHeight,
  }));
  const [panelSize, setPanelSize] = useState(() => {
    const w =
      typeof window === 'undefined' ? 1024 : Math.min(1024, window.innerWidth - 32);
    return { w, h: 80 };
  });

  const SAFE_PADDING = 16;

  const constraints = useMemo(() => {
    if (panelSize.w === 0 || panelSize.h === 0) {
      return { left: 0, right: 0, top: 0, bottom: 0 };
    }
    const halfW = panelSize.w / 2;
    const naturalLeft = viewport.w / 2 - halfW;
    const naturalRight = viewport.w / 2 + halfW;
    const naturalTop = viewport.h - 16 - panelSize.h;
    const naturalBottom = viewport.h - 16;

    return {
      left: SAFE_PADDING - naturalLeft,
      right: viewport.w - SAFE_PADDING - naturalRight,
      top: SAFE_PADDING - naturalTop,
      bottom: viewport.h - SAFE_PADDING - naturalBottom,
    };
  }, [viewport.w, viewport.h, panelSize.w, panelSize.h]);

  const clampToViewport = useCallback(
    (cx: number, cy: number) => ({
      x: Math.min(
        Math.max(constraints.left, cx),
        Math.max(constraints.left, constraints.right),
      ),
      y: Math.min(
        Math.max(constraints.top, cy),
        Math.max(constraints.top, constraints.bottom),
      ),
    }),
    [constraints],
  );

  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    if (panelSize.w === 0) return;
    hydratedRef.current = true;
    try {
      const raw = localStorage.getItem(LOUPE_POSITION_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        const clamped = clampToViewport(
          typeof saved?.x === 'number' ? saved.x : 0,
          typeof saved?.y === 'number' ? saved.y : 0,
        );
        x.set(clamped.x);
        y.set(clamped.y);
      }
    } catch {
      /* ignore */
    }
  }, [panelSize.w, clampToViewport, x, y]);

  useEffect(() => {
    const onResize = () => {
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Re-attach the ResizeObserver whenever the element under
  // `panelRef` changes. That happens on `expanded` toggles AND on
  // collapse/expand transitions (CollapsedPanel ↔ ActiveScenePanel
  // are different elements sharing the same ref). Without
  // `activeSceneId` in the deps, the observer stays bound to the
  // unmounted node after transition → `panelSize` keeps the old
  // (pill) dimensions → drag constraints stay permissive → the
  // newly-expanded panel renders past the safe area at edge
  // positions. Including `activeSceneId` here forces re-observation
  // against the new element on every swap.
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setPanelSize((prev) =>
        prev.w === rect.width && prev.h === rect.height
          ? prev
          : { w: rect.width, h: rect.height },
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [expanded, registry.activeSceneId]);

  useEffect(() => {
    if (panelSize.w === 0) return;
    const clamped = clampToViewport(x.get(), y.get());
    if (clamped.x !== x.get()) x.set(clamped.x);
    if (clamped.y !== y.get()) y.set(clamped.y);
  }, [clampToViewport, panelSize.w, panelSize.h, x, y]);

  const persistPosition = () => {
    try {
      localStorage.setItem(LOUPE_POSITION_KEY, JSON.stringify({ x: x.get(), y: y.get() }));
    } catch {
      /* ignore */
    }
  };

  // If the only registered scene is our built-in sample, treat the
  // panel as still effectively empty for "Try the sample" button
  // visibility logic. If the user wires up a real scene, sample
  // auto-removes — no point keeping a teaching aid alongside real work.
  const realSceneCount = registry.scenes.filter(
    (s) => s.id !== SAMPLE_SCENE_ID,
  ).length;
  useEffect(() => {
    if (sampleActive && realSceneCount > 0) setSampleActive(false);
  }, [sampleActive, realSceneCount]);
  const sampleNode = sampleActive ? (
    <SampleScene onDismiss={() => setSampleActive(false)} />
  ) : null;

  if (!activeScene) {
    // Two sub-states:
    //  - No scenes registered at all → static centered "no scene" pill
    //    (Loupe hasn't found anything to drive yet).
    //  - Scenes registered but user picked "None" → collapsed
    //    draggable pill with JUST the scene picker, so they can
    //    re-select any time. Shares the drag position with the
    //    full panel so collapsing/expanding doesn't jump around.
    if (registry.scenes.length === 0) {
      return (
        <>
          <Welcome />
          {sampleNode}
          <EmptyPanel
            onTrySample={() => {
              setSampleActive(true);
              // Pre-select the sample so when it registers the panel
              // expands to drive it — beats any sticky "None" pick
              // from a prior session.
              registry.setActiveSceneId(SAMPLE_SCENE_ID);
            }}
          />
        </>
      );
    }
    return (
      <>
        <Welcome />
        {sampleNode}
        <CollapsedPanel
        registry={registry}
        dragControls={dragControls}
        x={x}
        y={y}
        panelRef={panelRef}
        setPanelSize={setPanelSize}
        persistPosition={persistPosition}
        dragConstraints={constraints}
      />
      </>
    );
  }

  return (
    <>
    <Welcome />
    {sampleNode}
    <ActiveScenePanel
      registry={registry}
      activeScene={activeScene}
      pickerMode={pickerMode}
      setPickerMode={setPickerMode}
      annotationsVisible={annotationsVisible}
      setAnnotationsVisible={setAnnotationsVisible}
      annotations={annotations}
      expanded={expanded}
      setExpanded={setExpanded}
      dragControls={dragControls}
      x={x}
      y={y}
      persistPosition={persistPosition}
      dragConstraints={constraints}
      panelRef={panelRef}
      viewportW={viewport.w}
    />
    </>
  );
}

type Registry = ReturnType<typeof useLoupeRegistry>;
type ActiveScene = NonNullable<ReturnType<Registry['scenes']['find']>>;

/**
 * Empty-state panel — a skeleton/preview of the real ActiveScenePanel
 * shown when no scenes are registered. Same shape, same controls,
 * same rhythm — but all controls are inert and the scene picker
 * dropdown exposes a single "Try the sample" option that mounts
 * Loupe's built-in demo scene. The designer learns the panel's real
 * mechanics in the empty state.
 */
function EmptyPanel({ onTrySample }: { onTrySample: () => void }) {
  return (
    <div
      data-loupe-ui
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 16,
        transform: 'translateX(-50%)',
        width: 'min(1024px, calc(100vw - 32px))',
        zIndex: 10050,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          pointerEvents: 'auto',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          background: PANEL_BG,
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderRadius: 16,
          border: `1px solid ${PANEL_BORDER}`,
          boxShadow:
            '0 20px 40px rgba(0, 0, 0, 0.35), 0 4px 12px rgba(0, 0, 0, 0.25)',
          color: PANEL_FG,
          fontFamily: FONT,
          padding: '12px 14px 14px',
        }}
      >
        {/* Top row — picker, phase label, time, controls */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '4px 2px',
                color: '#6B7280',
              }}
              aria-hidden
            >
              <GripIcon />
            </span>

            <button
              type="button"
              onClick={onTrySample}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 999,
                border: `1px solid ${PANEL_BORDER}`,
                background: 'rgba(255, 255, 255, 0.04)',
                color: PANEL_FG,
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <span style={{ color: PANEL_MUTED, fontWeight: 500 }}>Empty —</span>
              <span style={{ color: ACCENT }}>try the sample</span>
            </button>

            <span
              style={{
                fontWeight: 700,
                fontSize: 13,
                letterSpacing: 0.2,
                color: PANEL_MUTED,
                opacity: 0.6,
              }}
            >
              —
            </span>
            <span
              style={{
                fontWeight: 500,
                fontSize: 11,
                color: PANEL_MUTED,
                fontVariantNumeric: 'tabular-nums',
                opacity: 0.6,
              }}
            >
              0ms / 0ms · 0%
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DisabledIconButton title="Add feedback on element">
              <PointerIcon />
            </DisabledIconButton>
            <DisabledIconButton title="Draw region">
              <RegionIcon />
            </DisabledIconButton>
            <DisabledIconButton title="Annotations">
              <EyeIcon />
            </DisabledIconButton>
            <span style={{ width: 1, height: 18, background: PANEL_BORDER, margin: '0 2px' }} />
            <DisabledIconButton title="Play / pause">
              <PlayIconShape />
            </DisabledIconButton>
            <DisabledIconButton title="Restart">
              <RestartIconShape />
            </DisabledIconButton>
          </div>
        </div>

        {/* Inert scrubber */}
        <div
          aria-hidden
          style={{
            height: 6,
            borderRadius: 999,
            background: 'rgba(255, 255, 255, 0.06)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '0%',
              background: ACCENT,
              opacity: 0.5,
            }}
          />
        </div>

        {/* Phase strip — single neutral band */}
        <div style={{ marginTop: 8, display: 'flex', gap: 4, height: 22 }}>
          <div
            style={{
              flex: 1,
              borderRadius: 6,
              background: 'rgba(255, 255, 255, 0.04)',
              border: `1px dashed ${PANEL_BORDER}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              color: PANEL_MUTED,
              letterSpacing: 0.3,
            }}
          >
            no phases yet
          </div>
        </div>
      </div>
    </div>
  );
}

function DisabledIconButton({
  children,
  title,
}: {
  children: React.ReactNode;
  title: string;
}) {
  return (
    <Tooltip label={title}>
      <span
        aria-disabled
        style={{
        width: 26,
        height: 26,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        background: 'rgba(255,255,255,0.04)',
        color: PANEL_MUTED,
        opacity: 0.55,
        cursor: 'not-allowed',
      }}
      >
        {children}
      </span>
    </Tooltip>
  );
}

function PlayIconShape() {
  return (
    <svg width="20" height="20" viewBox="0 0 14 14" fill="currentColor">
      <path d="M4 3l8 4-8 4z" />
    </svg>
  );
}

function RestartIconShape() {
  return (
    <svg width="20" height="20" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7a4 4 0 1 1 1.2 2.8" />
      <path d="M2 4v3h3" />
    </svg>
  );
}

/**
 * Compact, draggable pill shown when the user picks "None" from the
 * scene dropdown. Contains only the scene picker so they can re-
 * select a scene whenever they want. Shares the drag position
 * (x/y MotionValues) with the full panel so collapsing/expanding
 * doesn't jump the panel across the screen.
 */
function CollapsedPanel({
  registry,
  dragControls,
  x,
  y,
  panelRef,
  setPanelSize,
  persistPosition,
  dragConstraints,
}: {
  registry: Registry;
  dragControls: ReturnType<typeof useDragControls>;
  x: ReturnType<typeof useMotionValue<number>>;
  y: ReturnType<typeof useMotionValue<number>>;
  panelRef: React.MutableRefObject<HTMLDivElement | null>;
  setPanelSize: (size: { w: number; h: number }) => void;
  persistPosition: () => void;
  dragConstraints: { left: number; right: number; top: number; bottom: number };
}) {
  // Track the collapsed pill's size so constraints stay honest if
  // user drags to viewport edges.
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const update = () => {
      setPanelSize({ w: el.offsetWidth, h: el.offsetHeight });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [panelRef, setPanelSize]);

  return (
    <motion.div
      data-loupe-ui
      style={{
        // Inline styles only — Loupe ships no CSS, so we must not
        // rely on host utility classes (Tailwind `fixed` /
        // `pointer-events-none`). Without `position: fixed` here the
        // pill drops into normal document flow and renders below the
        // page content instead of floating at the bottom-center.
        position: 'fixed',
        pointerEvents: 'none',
        bottom: 16,
        left: '50%',
        zIndex: 10050,
        translateX: '-50%',
      }}
    >
      <motion.div
        ref={panelRef}
        drag
        dragListener={false}
        dragControls={dragControls}
        dragMomentum={false}
        dragElastic={0}
        dragConstraints={dragConstraints}
        onDragEnd={persistPosition}
        transition={{ duration: 0.35, ease: [0.59, 0.01, 0.4, 0.98] }}
        style={{
          pointerEvents: 'auto',
          x,
          y,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 8px 6px 10px',
          background: PANEL_BG,
          border: `1px solid ${PANEL_BORDER}`,
          borderRadius: 999,
          color: PANEL_FG,
          fontFamily: FONT,
          boxShadow:
            '0 12px 28px rgba(0, 0, 0, 0.30), 0 3px 10px rgba(0, 0, 0, 0.20)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
        }}
      >
        {/* Drag handle — grabs the whole pill */}
        <button
          type="button"
          aria-label="Drag Loupe"
          onPointerDown={(e) => dragControls.start(e)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            border: 'none',
            borderRadius: 999,
            background: 'transparent',
            color: PANEL_MUTED,
            cursor: 'grab',
            padding: 0,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden>
            <circle cx="3" cy="3" r="1.1" />
            <circle cx="3" cy="6" r="1.1" />
            <circle cx="3" cy="9" r="1.1" />
            <circle cx="9" cy="3" r="1.1" />
            <circle cx="9" cy="6" r="1.1" />
            <circle cx="9" cy="9" r="1.1" />
          </svg>
        </button>
        <ScenePicker registry={registry} />
      </motion.div>
    </motion.div>
  );
}

function ActiveScenePanel({
  registry,
  activeScene,
  pickerMode,
  setPickerMode,
  annotationsVisible,
  setAnnotationsVisible,
  annotations,
  expanded,
  setExpanded,
  dragControls,
  x,
  y,
  persistPosition,
  dragConstraints,
  panelRef,
  viewportW,
}: {
  registry: Registry;
  activeScene: ActiveScene;
  pickerMode: ReturnType<typeof useAnnotations>['pickerMode'];
  setPickerMode: ReturnType<typeof useAnnotations>['setPickerMode'];
  annotationsVisible: boolean;
  setAnnotationsVisible: (v: boolean) => void;
  annotations: ReturnType<typeof useAnnotations>['annotations'];
  expanded: boolean;
  setExpanded: (v: boolean) => void;
  dragControls: ReturnType<typeof useDragControls>;
  x: ReturnType<typeof useMotionValue<number>>;
  y: ReturnType<typeof useMotionValue<number>>;
  persistPosition: () => void;
  dragConstraints: { left: number; right: number; top: number; bottom: number };
  panelRef: React.MutableRefObject<HTMLDivElement | null>;
  viewportW: number;
}) {
  const isMobile = viewportW < 640;
  const tl = activeScene.timeline;
  const {
    time,
    ranges,
    totalDuration,
    phaseOrder,
    phaseLabels,
    speed,
    setSpeed,
    paused,
    setPaused,
    seek,
    restart,
  } = tl;

  const [displayMs, setDisplayMs] = useState(() => time.get());
  useEffect(() => {
    setDisplayMs(time.get());
  }, [time]);
  useMotionValueEvent(time, 'change', (v) => setDisplayMs(v));

  const phase = phaseAtTime(ranges, totalDuration, displayMs);
  const phaseRange = phase
    ? rangeOf(ranges, phase)
    : { start: 0, end: 0, duration: 0, phase: '' };
  const phaseElapsed = Math.max(0, displayMs - phaseRange.start);
  const totalProgress = totalDuration > 0 ? displayMs / totalDuration : 0;
  const phaseProgress = phaseRange.duration > 0 ? phaseElapsed / phaseRange.duration : 0;

  const stripRef = useRef<HTMLDivElement>(null);
  const setTimeFromPointer = (clientX: number) => {
    const el = stripRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    seek(ratio * totalDuration);
  };
  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    if (!paused) setPaused(true);
    setTimeFromPointer(e.clientX);
    const el = stripRef.current;
    if (el) el.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (e.buttons === 0) return;
    setTimeFromPointer(e.clientX);
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const el = stripRef.current;
    if (el && el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
  };

  const labelFor = (p: string) => phaseLabels?.[p] ?? p;

  return (
    <>
      <SceneFlashOverlay registry={registry} />
      <SceneActiveHalo registry={registry} />

      <motion.div
        data-loupe-ui
        style={{
          position: 'fixed',
          left: '50%',
          transform: 'translateX(-50%)',
          bottom: 16,
          width: 'min(1024px, calc(100vw - 32px))',
          zIndex: 10050,
          display: 'flex',
          pointerEvents: 'none',
        }}
      >
        <motion.div
          ref={panelRef}
          layout
          drag
          dragListener={false}
          dragControls={dragControls}
          dragMomentum={false}
          dragElastic={0}
          onDragEnd={persistPosition}
          dragConstraints={dragConstraints}
          transition={{ duration: 0.35, ease: [0.59, 0.01, 0.4, 0.98] }}
          style={{
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            x,
            y,
            background: PANEL_BG,
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            borderRadius: 16,
            border: `1px solid ${PANEL_BORDER}`,
            boxShadow:
              '0 20px 40px rgba(0, 0, 0, 0.35), 0 4px 12px rgba(0, 0, 0, 0.25)',
            color: PANEL_FG,
            fontFamily: FONT,
            overflow: 'hidden',
          }}
        >
          <AnimatePresence initial={false} mode="wait">
            {expanded ? (
              <motion.div
                key="expanded"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{ padding: isMobile ? '10px 10px 10px' : '12px 14px 14px' }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center',
                    justifyContent: isMobile ? undefined : 'space-between',
                    marginBottom: 10,
                    gap: isMobile ? 8 : 0,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobile ? 8 : 10,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Tooltip label="Drag to move the panel">
                      <span
                        onPointerDown={(e) => dragControls.start(e)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '4px 2px',
                          cursor: 'grab',
                          color: '#6B7280',
                          touchAction: 'none',
                        }}
                      >
                        <GripIcon />
                      </span>
                    </Tooltip>
                    <ScenePicker registry={registry} />
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: isMobile ? 12 : 13,
                        letterSpacing: 0.2,
                      }}
                    >
                      {phase}
                    </span>
                    <span
                      style={{
                        fontWeight: 500,
                        fontSize: isMobile ? 10 : 11,
                        color: PANEL_MUTED,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {isMobile ? (
                        <>
                          {formatMs(phaseElapsed)} / {formatMs(phaseRange.duration)}
                          {' · '}
                          {Math.round(totalProgress * 100)}%
                        </>
                      ) : (
                        <>
                          {formatMs(phaseElapsed)} / {formatMs(phaseRange.duration)}
                          {' · '}
                          {formatMs(displayMs)} / {formatMs(totalDuration)}
                          {' · '}
                          {Math.round(totalProgress * 100)}%
                        </>
                      )}
                    </span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobile ? 6 : 8,
                      flexWrap: isMobile ? 'wrap' : 'nowrap',
                      justifyContent: isMobile ? 'flex-start' : undefined,
                    }}
                  >
                    <IconButton
                      onClick={() =>
                        setPickerMode(pickerMode === 'element' ? 'off' : 'element')
                      }
                      title={pickerMode === 'element' ? 'Cancel picker' : 'Add feedback on element'}
                      active={pickerMode === 'element'}
                    >
                      <PointerIcon />
                    </IconButton>
                    <IconButton
                      onClick={() =>
                        setPickerMode(pickerMode === 'region' ? 'off' : 'region')
                      }
                      disabled={!paused && pickerMode !== 'region'}
                      title={paused ? 'Draw region (paused mode)' : 'Pause first to draw a region'}
                      active={pickerMode === 'region'}
                    >
                      <RegionIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => setAnnotationsVisible(!annotationsVisible)}
                      title={annotationsVisible ? 'Hide annotations' : 'Show annotations'}
                      active={!annotationsVisible}
                    >
                      {annotationsVisible ? <EyeIcon /> : <EyeOffIcon />}
                    </IconButton>
                    {annotations.length > 0 && (
                      <Tooltip
                        label={`${annotations.length} annotation${annotations.length === 1 ? '' : 's'} on this scene`}
                      >
                        <span
                          style={{
                            padding: '0 6px',
                            height: 18,
                            borderRadius: 999,
                            background: ACCENT,
                            color: '#121419',
                            fontSize: 10,
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                          }}
                        >
                          {annotations.length}
                        </span>
                      </Tooltip>
                    )}
                    <span
                      style={{
                        width: 1,
                        height: 16,
                        background: 'rgba(255,255,255,0.12)',
                        margin: '0 2px',
                      }}
                    />
                    {isMobile ? (
                      <CompactSpeedChip speed={speed} onChange={setSpeed} />
                    ) : (
                      <SpeedSelector speed={speed} onChange={setSpeed} />
                    )}
                    <IconButton
                      onClick={() => setPaused(!paused)}
                      title={paused ? 'Play' : 'Pause'}
                    >
                      {paused ? <PlayIcon /> : <PauseIcon />}
                    </IconButton>
                    <IconButton onClick={restart} title="Restart">
                      <RestartIcon />
                    </IconButton>
                    <IconButton onClick={() => setExpanded(false)} title="Collapse">
                      <CollapseIcon />
                    </IconButton>
                  </div>
                </div>

                <div
                  ref={stripRef}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onPointerCancel={onPointerUp}
                  style={{
                    position: 'relative',
                    display: 'flex',
                    height: 26,
                    borderRadius: 8,
                    background: 'rgba(255, 255, 255, 0.04)',
                    overflow: 'hidden',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    cursor: 'ew-resize',
                    touchAction: 'none',
                    userSelect: 'none',
                  }}
                >
                  {phaseOrder.map((p) => {
                    const dur = ranges.find((r) => r.phase === p)?.duration ?? 0;
                    const widthPct = totalDuration > 0 ? (dur / totalDuration) * 100 : 0;
                    const isActive = p === phase;
                    return (
                      <Tooltip key={p} label={`${labelFor(p)} · ${formatMs(dur)} · jump here`}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          seek(rangeOf(ranges, p).start);
                        }}
                        style={{
                          flex: `0 0 ${widthPct}%`,
                          minWidth: 0,
                          height: '100%',
                          padding: '0 6px',
                          margin: 0,
                          border: 'none',
                          borderRight: '1px solid rgba(0, 0, 0, 0.35)',
                          background: isActive ? ACCENT_SOFT : 'transparent',
                          color: isActive ? PANEL_HIGHLIGHT : PANEL_MUTED,
                          fontFamily: 'inherit',
                          fontWeight: isActive ? 700 : 500,
                          fontSize: 10,
                          lineHeight: 1,
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'background 150ms ease, color 150ms ease',
                        }}
                      >
                        {labelFor(p)}
                      </button>
                      </Tooltip>
                    );
                  })}

                  <div
                    aria-hidden
                    style={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: `${totalProgress * 100}%`,
                      width: 2,
                      background: ACCENT,
                      boxShadow: `0 0 8px ${ACCENT_GLOW}`,
                      pointerEvents: 'none',
                    }}
                  />
                </div>

                <div
                  style={{
                    marginTop: 8,
                    height: 2,
                    borderRadius: 999,
                    background: 'rgba(255, 255, 255, 0.06)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.min(phaseProgress, 1) * 100}%`,
                      background: ACCENT_TINT,
                    }}
                  />
                </div>

                {annotations.length > 0 && <AnnotationList />}
              </motion.div>
            ) : (
              <motion.div
                key="collapsed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: isMobile ? '8px 10px' : '8px 14px',
                  gap: isMobile ? 8 : 10,
                  width: '100%',
                  color: 'inherit',
                  fontFamily: 'inherit',
                }}
              >
                <Tooltip label="Drag to move the panel">
                  <span
                    onPointerDown={(e) => dragControls.start(e)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: '#6B7280',
                      cursor: 'grab',
                      touchAction: 'none',
                    }}
                  >
                    <GripIcon />
                  </span>
                </Tooltip>
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    padding: 0,
                    gap: isMobile ? 8 : 10,
                    background: 'transparent',
                    border: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: 12,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {activeScene.label}
                  </span>
                  <span
                    style={{
                      fontWeight: 500,
                      fontSize: 11,
                      color: PANEL_MUTED,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minWidth: 0,
                      flexShrink: 1,
                    }}
                  >
                    {isMobile ? labelFor(phase) : phase}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 20,
                      height: 3,
                      borderRadius: 999,
                      background: 'rgba(255, 255, 255, 0.08)',
                      overflow: 'hidden',
                    }}
                  >
                    <span
                      style={{
                        display: 'block',
                        height: '100%',
                        width: `${Math.min(totalProgress, 1) * 100}%`,
                        background: ACCENT,
                      }}
                    />
                  </span>
                  <span style={{ fontSize: 10, color: PANEL_MUTED, flexShrink: 0 }}>
                    {Math.round(totalProgress * 100)}%
                  </span>
                  <ExpandIcon />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </>
  );
}

function ScenePicker({ registry }: { registry: Registry }) {
  const [open, setOpen] = useState(false);
  // `buttonRef` is the trigger; `menuRef` is the portalled popover.
  // Keeping both refs so the outside-click handler knows to ignore
  // clicks that land inside either (otherwise the popover closes
  // the instant the user clicks an item).
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ left: number; top: number } | null>(null);
  const active = registry.scenes.find((s) => s.id === registry.activeSceneId);

  // Recompute the popover anchor each time it opens (or the viewport
  // resizes). The panel has `overflow: hidden` on its outer wrapper
  // — any absolute child gets clipped — so the menu lives in a
  // React portal outside that container and is anchored via fixed
  // coords pulled from the trigger button's bounding rect.
  useEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    const update = () => {
      const btn = buttonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      setMenuPos({ left: rect.left, top: rect.top });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (buttonRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div style={{ position: 'relative' }}>
      <Tooltip label="Pick which animation Loupe is controlling">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: 'rgba(255,255,255,0.06)',
          color: PANEL_HIGHLIGHT,
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 999,
          padding: '4px 10px',
          fontFamily: 'inherit',
          fontWeight: 700,
          fontSize: 11,
          cursor: 'pointer',
          letterSpacing: 0.2,
        }}
      >
        <span>{active?.label ?? '— Pick a scene'}</span>
        <svg width="9" height="9" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 5l4 4 4-4" />
        </svg>
      </button>
      </Tooltip>
      {open && menuPos &&
        typeof document !== 'undefined' &&
        createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            // Float above the trigger — translateY pulls the menu
            // back by its own height + 6px so its bottom edge sits
            // 6px above the button's top edge.
            left: menuPos.left,
            top: menuPos.top,
            transform: 'translateY(calc(-100% - 6px))',
            minWidth: 180,
            background: '#121419',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 10,
            padding: 4,
            boxShadow: '0 12px 30px rgba(0,0,0,0.4)',
            zIndex: 2147483647,
            color: PANEL_FG,
            fontFamily: FONT,
          }}
        >
          {registry.scenes.length === 0 && (
            <div style={{ padding: '8px 10px', color: '#6B7280', fontSize: 11 }}>
              No scenes registered
            </div>
          )}
          {registry.scenes.length > 0 && (
            <>
              {/* "None" option — collapses Loupe to a draggable
                  pill so it's out of the way without being fully
                  dismissed. */}
              <button
                type="button"
                onClick={() => {
                  registry.setActiveSceneId(null);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  padding: '7px 10px',
                  borderRadius: 6,
                  border: 'none',
                  background:
                    registry.activeSceneId === null
                      ? 'rgba(58,151,249,0.18)'
                      : 'transparent',
                  color:
                    registry.activeSceneId === null ? PANEL_HIGHLIGHT : PANEL_MUTED,
                  fontFamily: 'inherit',
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: 'pointer',
                  textAlign: 'left',
                  gap: 8,
                  fontStyle: 'italic',
                }}
                onMouseEnter={(e) => {
                  if (registry.activeSceneId !== null)
                    (e.currentTarget as HTMLElement).style.background =
                      'rgba(255,255,255,0.06)';
                }}
                onMouseLeave={(e) => {
                  if (registry.activeSceneId !== null)
                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.12)',
                    border: '1px dashed rgba(255,255,255,0.3)',
                    flexShrink: 0,
                  }}
                />
                <span style={{ flex: 1 }}>— None (collapse panel)</span>
              </button>
              <div
                aria-hidden
                style={{
                  height: 1,
                  background: 'rgba(255,255,255,0.08)',
                  margin: '4px 6px',
                }}
              />
            </>
          )}
          {registry.scenes.map((s) => {
            const isActive = s.id === registry.activeSceneId;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  registry.setActiveSceneId(s.id);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  padding: '7px 10px',
                  borderRadius: 6,
                  border: 'none',
                  background: isActive ? 'rgba(58,151,249,0.18)' : 'transparent',
                  color: isActive ? PANEL_HIGHLIGHT : PANEL_FG,
                  fontFamily: 'inherit',
                  fontWeight: isActive ? 700 : 600,
                  fontSize: 12,
                  cursor: 'pointer',
                  textAlign: 'left',
                  gap: 8,
                }}
                onMouseEnter={(e) => {
                  if (!isActive)
                    (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)';
                }}
                onMouseLeave={(e) => {
                  if (!isActive)
                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    background: isActive ? ACCENT : 'rgba(255,255,255,0.18)',
                    flexShrink: 0,
                  }}
                />
                <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {s.label}
                  {s.id === 'loupe-demo' && (
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        padding: '1px 5px',
                        borderRadius: 4,
                        background: 'rgba(255,255,255,0.1)',
                        color: 'rgba(255,255,255,0.55)',
                        textTransform: 'uppercase',
                      }}
                    >
                      Demo
                    </span>
                  )}
                </span>
                <span style={{ color: '#6B7280', fontWeight: 500, fontSize: 10 }}>{s.id}</span>
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}

function SceneFlashOverlay({ registry }: { registry: Registry }) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const lastTickRef = useRef<number>(registry.flashTick);

  useEffect(() => {
    if (registry.flashTick === lastTickRef.current) return;
    lastTickRef.current = registry.flashTick;
    const scene = registry.scenes.find((s) => s.id === registry.activeSceneId);
    const el = scene?.rootRef.current;
    if (!el) {
      setRect(null);
      return;
    }
    // Scroll the scene into view first so the flash and the
    // animation itself are actually visible. `block: 'center'`
    // frames the scene rather than jamming its top against the
    // viewport.
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch {
      el.scrollIntoView();
    }
    // Wait for the smooth scroll to ACTUALLY stop, not a fixed
    // 400ms guess — smooth-scroll duration varies by browser and
    // distance. Poll every frame and only fire once the rect has
    // been identical for ~120ms. Otherwise the flash paints
    // mid-scroll at a stale position and the rest of the fade
    // plays out there while the page keeps moving.
    let raf = 0;
    let clearTimer = 0;
    let lastMeasured: DOMRect | null = null;
    let stableSince = 0;
    let fired = false;
    const STABLE_MS = 120;
    const STABLE_TIMEOUT_MS = 1500;
    const startedAt = performance.now();

    const tick = () => {
      const now = performance.now();
      const next = el.getBoundingClientRect();
      if (
        lastMeasured &&
        lastMeasured.left === next.left &&
        lastMeasured.top === next.top &&
        lastMeasured.width === next.width &&
        lastMeasured.height === next.height
      ) {
        if (stableSince === 0) stableSince = now;
        if (now - stableSince >= STABLE_MS) {
          fired = true;
          setRect(next);
          clearTimer = window.setTimeout(() => setRect(null), 750);
          return;
        }
      } else {
        stableSince = 0;
        lastMeasured = next;
      }
      if (now - startedAt > STABLE_TIMEOUT_MS) {
        fired = true;
        setRect(next);
        clearTimer = window.setTimeout(() => setRect(null), 750);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      if (clearTimer) window.clearTimeout(clearTimer);
      // If we never fired (e.g., user re-selected before settle),
      // silently drop — no stale flash will paint.
      void fired;
    };
  }, [registry.flashTick, registry.activeSceneId, registry.scenes]);

  if (!rect) return null;
  return (
    <motion.div
      data-loupe-ui
      aria-hidden
      initial={{ opacity: 0, scale: 0.985 }}
      animate={{ opacity: [0, 1, 1, 0], scale: [0.985, 1, 1, 1] }}
      transition={{ duration: 0.7, times: [0, 0.18, 0.6, 1], ease: [0.59, 0.01, 0.4, 0.98] }}
      style={{
        position: 'fixed',
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        pointerEvents: 'none',
        border: `3px solid ${ACCENT}`,
        borderRadius: 8,
        boxShadow: `0 0 0 4px ${ACCENT_RING}, 0 0 32px 8px ${ACCENT_HALO}`,
        boxSizing: 'border-box',
        zIndex: 10049,
      }}
    />
  );
}

/**
 * Persistent, subtle halo around the active scene's root. Lives for
 * as long as a scene is active — answers "what am I scrubbing right
 * now?" at a glance. The momentary `SceneFlashOverlay` sits on top
 * during selection; this layer stays after the flash fades.
 */
function SceneActiveHalo({ registry }: { registry: Registry }) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [radius, setRadius] = useState<string>('12px');
  const [visible, setVisible] = useState(false);
  const activeId = registry.activeSceneId;

  useLayoutEffect(() => {
    if (!activeId) {
      setRect(null);
      setVisible(false);
      return;
    }
    const scene = registry.scenes.find((s) => s.id === activeId);
    const el = scene?.rootRef.current;
    if (!el) {
      setRect(null);
      setVisible(false);
      return;
    }

    // Don't paint until the scroll-into-view (triggered by the flash
    // overlay on the same `activeSceneId` change) has settled. Smooth
    // scroll duration varies wildly by browser and distance, so we
    // detect stability instead of timing it: poll the rect every
    // frame, and only reveal once it has been identical for ~120ms.
    // That's "the scroll has actually stopped" — robust to short
    // and long scroll distances both.
    setVisible(false);

    const rectsEqual = (a: DOMRect, b: DOMRect) =>
      a.left === b.left &&
      a.top === b.top &&
      a.width === b.width &&
      a.height === b.height;

    const reveal = () => {
      try {
        setRadius(window.getComputedStyle(el).borderRadius || '12px');
      } catch {
        setRadius('12px');
      }
      const next = el.getBoundingClientRect();
      setRect((prev) => (prev && rectsEqual(prev, next) ? prev : next));
      setVisible(true);
    };

    // Phase 1 — wait for the scroll-into-view (triggered on the same
    // activeSceneId change) to ACTUALLY settle, then reveal and STOP
    // the rAF. Critically, this loop terminates: it does NOT keep
    // measuring forever. A permanently-running rAF that calls
    // getBoundingClientRect every frame forces a synchronous reflow
    // each frame and keeps the page from ever going idle, which
    // starves the main thread and makes the panel feel unresponsive.
    let settleRaf = 0;
    let lastRect: DOMRect | null = null;
    let stableSince = 0;
    const STABLE_MS = 120;
    const STABLE_TIMEOUT_MS = 1500;
    const startedAt = performance.now();

    const settle = () => {
      const now = performance.now();
      const next = el.getBoundingClientRect();
      if (lastRect && rectsEqual(lastRect, next)) {
        if (stableSince === 0) stableSince = now;
        if (now - stableSince >= STABLE_MS) {
          reveal();
          settleRaf = 0;
          return;
        }
      } else {
        stableSince = 0;
        lastRect = next;
      }
      // Hard cap so a constantly-shifting scene still eventually shows
      // the halo. Better one-frame jitter than no halo at all.
      if (now - startedAt > STABLE_TIMEOUT_MS) {
        reveal();
        settleRaf = 0;
        return;
      }
      settleRaf = requestAnimationFrame(settle);
    };
    settleRaf = requestAnimationFrame(settle);

    // Phase 2 — once revealed, only track the target while it's
    // actually moving (page scroll, resize). Coalesce to a single
    // rAF and STOP a few stable frames after motion ceases, so an
    // idle page runs no rAF and does no per-frame layout work.
    let trackRaf = 0;
    let trackLast: DOMRect | null = null;
    let trackStableFrames = 0;
    const track = () => {
      const next = el.getBoundingClientRect();
      setRect((prev) => (prev && rectsEqual(prev, next) ? prev : next));
      if (trackLast && rectsEqual(trackLast, next)) {
        trackStableFrames += 1;
      } else {
        trackStableFrames = 0;
      }
      trackLast = next;
      if (trackStableFrames >= 3) {
        trackRaf = 0;
        return;
      }
      trackRaf = requestAnimationFrame(track);
    };

    const schedule = () => {
      // Before reveal, just keep Phase 1 waiting for stability.
      if (settleRaf) {
        stableSince = 0;
        return;
      }
      // After reveal, (re)start the short tracking burst.
      trackStableFrames = 0;
      if (!trackRaf) trackRaf = requestAnimationFrame(track);
    };

    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    window.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (settleRaf) cancelAnimationFrame(settleRaf);
      if (trackRaf) cancelAnimationFrame(trackRaf);
      ro.disconnect();
      window.removeEventListener('scroll', schedule, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', schedule);
    };
  }, [activeId, registry.scenes]);

  if (!rect) return null;
  return (
    <div
      data-loupe-ui
      aria-hidden
      style={{
        position: 'fixed',
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        pointerEvents: 'none',
        borderRadius: radius,
        boxShadow: `0 0 0 1px ${ACCENT_RING}, 0 0 24px 2px ${ACCENT_RING}`,
        boxSizing: 'border-box',
        zIndex: 10048,
        opacity: visible ? 1 : 0,
        transition: 'opacity 220ms ease',
      }}
    />
  );
}

function SpeedSelector({
  speed,
  onChange,
}: {
  speed: number;
  onChange: (s: number) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        background: 'rgba(255, 255, 255, 0.06)',
        borderRadius: 999,
        padding: 2,
        gap: 2,
      }}
    >
      {SPEED_OPTIONS.map((s) => {
        const active = s === speed;
        return (
          <button
            key={s}
            type="button"
            onClick={() => onChange(s)}
            style={{
              padding: '3px 8px',
              borderRadius: 999,
              border: 'none',
              fontFamily: 'inherit',
              fontWeight: active ? 700 : 500,
              fontSize: 10,
              lineHeight: 1,
              color: active ? '#121419' : PANEL_MUTED,
              background: active ? PANEL_HIGHLIGHT : 'transparent',
              cursor: 'pointer',
              transition: 'background 150ms ease, color 150ms ease',
            }}
          >
            {s === 1 ? '1×' : `${s}×`}
          </button>
        );
      })}
    </div>
  );
}

function CompactSpeedChip({
  speed,
  onChange,
}: {
  speed: number;
  onChange: (s: number) => void;
}) {
  return (
    <Tooltip label={`Playback speed · click to cycle`}>
      <button
        type="button"
        onClick={() => {
          const idx = SPEED_OPTIONS.indexOf(speed as (typeof SPEED_OPTIONS)[number]);
          const next = SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length];
          onChange(next);
        }}
        style={{
          minWidth: 32,
          height: 22,
          padding: '0 8px',
          borderRadius: 999,
          border: 'none',
          fontFamily: 'inherit',
          fontWeight: 700,
          fontSize: 11,
          lineHeight: 1,
          color: PANEL_HIGHLIGHT,
          background: 'rgba(255, 255, 255, 0.08)',
          cursor: 'pointer',
          transition: 'background 150ms ease, color 150ms ease',
        }}
      >
        {speed === 1 ? '1×' : `${speed}×`}
      </button>
    </Tooltip>
  );
}

function IconButton({
  children,
  onClick,
  title,
  shortcut,
  active = false,
  disabled = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  shortcut?: string;
  active?: boolean;
  disabled?: boolean;
}) {
  const baseBg = active ? ACCENT : 'rgba(255, 255, 255, 0.06)';
  const hoverBg = active ? ACCENT : 'rgba(255, 255, 255, 0.12)';
  const fg = active ? '#fff' : disabled ? '#4B5563' : PANEL_FG;
  return (
    <Tooltip label={title} shortcut={shortcut}>
      <button
        type="button"
        onClick={onClick}
        aria-label={title}
        disabled={disabled}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 26,
          height: 26,
          borderRadius: 999,
          border: 'none',
          background: baseBg,
          color: fg,
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'background 150ms ease, color 150ms ease',
          opacity: disabled ? 0.5 : 1,
        }}
        onMouseEnter={(e) => {
          if (!disabled) (e.currentTarget as HTMLElement).style.background = hoverBg;
        }}
        onMouseLeave={(e) => {
          if (!disabled) (e.currentTarget as HTMLElement).style.background = baseBg;
        }}
      >
        {children}
      </button>
    </Tooltip>
  );
}

function PlayIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 14 14" fill="none">
      <path d="M3 1.5L12 7L3 12.5V1.5Z" fill="currentColor" />
    </svg>
  );
}
function PauseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 14 14" fill="none">
      <rect x="2" y="1" width="3.5" height="12" rx="1" fill="currentColor" />
      <rect x="8.5" y="1" width="3.5" height="12" rx="1" fill="currentColor" />
    </svg>
  );
}
function RestartIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 14 14" fill="none">
      <path d="M2 2V6H6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2.5 6A5 5 0 1 1 3.5 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
function CollapseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 5l4 4 4-4" />
    </svg>
  );
}
function ExpandIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l4-4 4 4" />
    </svg>
  );
}
/**
 * The "Add feedback on element" entry point — a crosshair targeting
 * a circle. Reads as "pick a target on screen."
 */
function PointerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M12 7.99609C9.79218 7.99609 7.99609 9.79218 7.99609 12C7.99609 14.2078 9.79218 16.0039 12 16.0039C14.2078 16.0039 16.0039 14.2078 16.0039 12C16.0039 9.7922 14.2078 7.99609 12 7.99609ZM12 14.8276C10.441 14.8276 9.17246 13.5591 9.17246 12C9.17246 10.4409 10.441 9.17246 12 9.17246C13.559 9.17246 14.8275 10.4409 14.8275 12C14.8275 13.5591 13.559 14.8276 12 14.8276Z" />
      <path d="M21.4108 11.414H19.5366C19.2505 7.70525 16.2932 4.74869 12.5844 4.46257V2.58817C12.5844 2.2633 12.3235 2 11.9988 2C11.6741 2 11.4132 2.2633 11.4132 2.58817V4.46257C7.70443 4.74867 4.74949 7.70525 4.46335 11.414H2.58915C2.26442 11.414 2.00098 11.6748 2.00098 11.9996C2.00098 12.3245 2.26444 12.5852 2.58915 12.5852H4.46339C4.74951 16.294 7.70447 19.2494 11.4132 19.5355V21.4099C11.4132 21.7348 11.6741 21.9981 11.9988 21.9981C12.3236 21.9981 12.5844 21.7348 12.5844 21.4099V19.5355C16.2932 19.2494 19.2505 16.294 19.5366 12.5852H21.4109C21.7356 12.5852 21.999 12.3245 21.999 11.9996C21.999 11.6748 21.7356 11.414 21.4108 11.414ZM12 18.3818C8.48042 18.3818 5.61703 15.5184 5.61703 11.999C5.61703 8.47962 8.48042 5.61623 12 5.61623C15.5196 5.61623 18.383 8.47962 18.383 11.999C18.383 15.5184 15.5196 18.3818 12 18.3818Z" />
    </svg>
  );
}
/**
 * Region / marquee-select tool. Dotted-perimeter rectangle with a "+"
 * tucked in the bottom-right — reads as "select an area and add."
 */
function RegionIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M13 18C13 17.4477 13.4477 17 14 17H17V14C17 13.4477 17.4477 13 18 13C18.5523 13 19 13.4477 19 14V17H22C22.5523 17 23 17.4477 23 18C23 18.5523 22.5523 19 22 19H19V22C19 22.5523 18.5523 23 18 23C17.4477 23 17 22.5523 17 22V19H14C13.4477 19 13 18.5523 13 18ZM10 17C10.5523 17 11 17.4477 11 18C11 18.5523 10.5523 19 10 19C9.44772 19 9 18.5523 9 18C9 17.4477 9.44772 17 10 17ZM6 17C6.55228 17 7 17.4477 7 18C7 18.5523 6.55228 19 6 19C5.44772 19 5 18.5523 5 18C5 17.4477 5.44772 17 6 17ZM18 9C18.5523 9 19 9.44772 19 10C19 10.5523 18.5523 11 18 11C17.4477 11 17 10.5523 17 10C17 9.44772 17.4477 9 18 9ZM18 5C18.5523 5 19 5.44772 19 6C19 6.55228 18.5523 7 18 7C17.4477 7 17 6.55228 17 6C17 5.44772 17.4477 5 18 5ZM14 5C14.5523 5 15 5.44772 15 6C15 6.55228 14.5523 7 14 7C13.4477 7 13 6.55228 13 6C13 5.44772 13.4477 5 14 5ZM10 5C10.5523 5 11 5.44772 11 6C11 6.55228 10.5523 7 10 7C9.44772 7 9 6.55228 9 6C9 5.44772 9.44772 5 10 5ZM6 5C6.55228 5 7 5.44772 7 6C7 6.55228 6.55228 7 6 7C5.44772 7 5 6.55228 5 6C5 5.44772 5.44772 5 6 5ZM6 13C6.55228 13 7 13.4477 7 14C7 14.5523 6.55228 15 6 15C5.44772 15 5 14.5523 5 14C5 13.4477 5.44772 13 6 13ZM6 9C6.55228 9 7 9.44772 7 10C7 10.5523 6.55228 11 6 11C5.44772 11 5 10.5523 5 10C5 9.44772 5.44772 9 6 9Z" />
    </svg>
  );
}
function EyeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.0" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 7s2-4 6-4 6 4 6 4-2 4-6 4-6-4-6-4z" />
      <circle cx="7" cy="7" r="1.8" />
    </svg>
  );
}
function EyeOffIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.0" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 2l10 10" />
      <path d="M4.2 4.3C2.5 5.3 1 7 1 7s2 4 6 4c1.1 0 2-.2 2.8-.6M11 10.2C12.3 9.3 13 7 13 7s-2-4-6-4c-.6 0-1.1.1-1.7.2" />
    </svg>
  );
}
function GripIcon() {
  return (
    <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor" aria-hidden>
      <circle cx="2" cy="3" r="1" />
      <circle cx="2" cy="7" r="1" />
      <circle cx="2" cy="11" r="1" />
      <circle cx="8" cy="3" r="1" />
      <circle cx="8" cy="7" r="1" />
      <circle cx="8" cy="11" r="1" />
    </svg>
  );
}

function formatMs(ms: number) {
  if (!Number.isFinite(ms)) return '∞';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function AnnotationList() {
  const { annotations, focusAnnotation, deleteAnnotation, clearAll } = useAnnotations();
  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    const md = annotationsToMarkdown(annotations);
    try {
      await navigator.clipboard.writeText(md);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = md;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
      } catch {
        /* ignore */
      } finally {
        document.body.removeChild(ta);
      }
    }
  };
  return (
    <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 10 }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: 0,
          background: 'transparent',
          border: 'none',
          color: PANEL_FG,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.2 }}>
          Annotations
          <span style={{ fontWeight: 500, color: PANEL_MUTED, marginLeft: 6 }}>
            {annotations.length}
          </span>
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {annotations.length > 0 && (
            <Tooltip label="Copy every annotation as a Markdown bullet list">
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  onCopy();
                }}
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: copied ? '#86EFAC' : PANEL_HIGHLIGHT,
                  cursor: 'pointer',
                  background: copied ? 'rgba(34, 197, 94, 0.15)' : ACCENT,
                  padding: '2px 8px',
                  borderRadius: 999,
                }}
              >
                {copied ? 'copied ✓' : 'copy feedback'}
              </span>
            </Tooltip>
          )}
          {annotations.length > 0 && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                if (confirm('Clear all annotations for this scene?')) clearAll();
              }}
              style={{ fontSize: 10, fontWeight: 600, color: PANEL_MUTED, cursor: 'pointer' }}
            >
              clear all
            </span>
          )}
          <span
            style={{
              color: PANEL_MUTED,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 150ms',
            }}
          >
            <svg width="10" height="10" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 5l4 4 4-4" />
            </svg>
          </span>
        </div>
      </button>
      {open && (
        <div
          style={{
            marginTop: 8,
            maxHeight: 180,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {annotations.map((a, i) => (
            <div
              key={a.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                padding: '6px 8px',
                borderRadius: 8,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  width: 20,
                  height: 20,
                  borderRadius: 999,
                  background: a.color,
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {i + 1}
              </span>
              <button
                type="button"
                onClick={() => focusAnnotation(a.id)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  color: PANEL_FG,
                  fontFamily: 'inherit',
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 700 }}>
                  {a.componentName || a.tagName || 'Region'}
                  <span style={{ fontWeight: 500, color: PANEL_MUTED, marginLeft: 6 }}>
                    {a.phase} · {Math.round(a.phaseElapsedMs)}ms
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: PANEL_MUTED,
                    marginTop: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {a.note}
                </div>
              </button>
              <Tooltip label="Delete this annotation">
                <button
                  type="button"
                  onClick={() => {
                    if (confirm(`Delete annotation #${i + 1}?`)) deleteAnnotation(a.id);
                  }}
                  style={{
                    flexShrink: 0,
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    background: 'transparent',
                    border: 'none',
                    color: '#6B7280',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: 12,
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </Tooltip>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
